import { ReportDatasetKind, ReportValueType } from "@ibada/shared-types/reporting"

import type { NormalizedReport } from "@/features/reports/model/normalized-report"
import type {
  ActivityBudgetPerformanceResponse,
  DepartmentSummaryResponse,
  FundActivityResponse,
  FundUtilizationResponse,
  ReconciliationSummaryResponse,
} from "@/features/reports/services/finance-resource-api"

/** Minimal meta for adapted finance-resource reports (they don't come from the reporting engine registry). */
function meta(entity: string) {
  return {
    executionId: `finance-resource:${entity}`,
    baseEntity: entity,
    datasetKind: ReportDatasetKind.SUMMARY,
    grainLabelKey: "finance.reports.grain.summary",
  }
}

/** Reconciliation health rollup → summary cards (proven vs pending accounts, open sessions, oldest gap). */
export function adaptReconciliationSummary(data: ReconciliationSummaryResponse): NormalizedReport {
  return {
    meta: meta("reconciliation"),
    summaryCards: [
      { key: "totalAccounts", labelKey: "finance.reports.resource.reconciliation.totalAccounts", value: data.totalAccounts, valueType: ReportValueType.NUMBER },
      { key: "reconciled", labelKey: "finance.reports.resource.reconciliation.reconciled", value: data.reconciled, valueType: ReportValueType.NUMBER },
      { key: "pending", labelKey: "finance.reports.resource.reconciliation.pending", value: data.pending, valueType: ReportValueType.NUMBER },
      { key: "openSessions", labelKey: "finance.reports.resource.reconciliation.openSessions", value: data.openSessions, valueType: ReportValueType.NUMBER },
      { key: "oldestUnreconciled", labelKey: "finance.reports.resource.reconciliation.oldestUnreconciled", value: data.oldestUnreconciledDate, valueType: ReportValueType.DATE },
    ],
  }
}

/** Per-fund utilization → a table of received / spent / available / utilization % / restricted. */
export function adaptFundUtilization(data: FundUtilizationResponse): NormalizedReport {
  return {
    meta: { ...meta("fund"), datasetKind: ReportDatasetKind.GROUPED },
    columns: [
      { key: "fundName", labelKey: "finance.reports.resource.fundUtilization.fund", source: "fund", valueType: ReportValueType.TEXT },
      { key: "received", labelKey: "finance.reports.resource.fundUtilization.received", source: "fund", valueType: ReportValueType.CURRENCY },
      { key: "spent", labelKey: "finance.reports.resource.fundUtilization.spent", source: "fund", valueType: ReportValueType.CURRENCY },
      { key: "available", labelKey: "finance.reports.resource.fundUtilization.available", source: "fund", valueType: ReportValueType.CURRENCY },
      { key: "utilizationPct", labelKey: "finance.reports.resource.fundUtilization.utilization", source: "fund", valueType: ReportValueType.PERCENTAGE },
      { key: "restricted", labelKey: "finance.reports.resource.fundUtilization.restricted", source: "fund", valueType: ReportValueType.BOOLEAN },
    ],
    rows: data.items.map((item) => ({
      fundName: item.fundName,
      received: item.received,
      spent: item.spent,
      available: item.available,
      utilizationPct: item.utilizationPct,
      restricted: item.restricted,
    })),
  }
}

/** Per-fund period ledger → opening/received/spent/closing cards + a dated movements table. */
export function adaptFundActivity(data: FundActivityResponse): NormalizedReport {
  return {
    meta: { ...meta("fund"), datasetKind: ReportDatasetKind.DETAIL },
    summaryCards: [
      { key: "opening", labelKey: "finance.reports.resource.fundActivity.opening", value: data.opening, valueType: ReportValueType.CURRENCY },
      { key: "received", labelKey: "finance.reports.resource.fundActivity.received", value: data.received, valueType: ReportValueType.CURRENCY },
      { key: "spent", labelKey: "finance.reports.resource.fundActivity.spent", value: data.spent, valueType: ReportValueType.CURRENCY },
      { key: "closing", labelKey: "finance.reports.resource.fundActivity.closing", value: data.closing, valueType: ReportValueType.CURRENCY },
    ],
    columns: [
      { key: "date", labelKey: "finance.reports.columns.date", source: "movement", valueType: ReportValueType.DATE },
      { key: "reference", labelKey: "finance.reports.columns.reference", source: "movement", valueType: ReportValueType.TEXT },
      { key: "type", labelKey: "finance.reports.resource.fundActivity.movementType", source: "movement", valueType: ReportValueType.TEXT },
      { key: "amount", labelKey: "finance.reports.resource.fundActivity.amount", source: "movement", valueType: ReportValueType.CURRENCY },
    ],
    rows: data.movements.map((movement) => ({
      date: movement.date,
      reference: movement.reference,
      type: movement.type,
      amount: movement.amount,
    })),
  }
}

/** Activity budget performance → planned/actual/variance summary cards + a per-line table. */
export function adaptActivityBudgetPerformance(data: ActivityBudgetPerformanceResponse): NormalizedReport {
  return {
    meta: { ...meta("budget"), datasetKind: ReportDatasetKind.GROUPED },
    summaryCards: [
      { key: "plannedIncome", labelKey: "finance.reports.resource.activityBudget.plannedIncome", value: data.totals.plannedIncome, valueType: ReportValueType.CURRENCY },
      { key: "actualIncome", labelKey: "finance.reports.resource.activityBudget.actualIncome", value: data.totals.actualIncome, valueType: ReportValueType.CURRENCY },
      { key: "plannedExpenses", labelKey: "finance.reports.resource.activityBudget.plannedExpenses", value: data.totals.plannedExpenses, valueType: ReportValueType.CURRENCY },
      { key: "actualExpenses", labelKey: "finance.reports.resource.activityBudget.actualExpenses", value: data.totals.actualExpenses, valueType: ReportValueType.CURRENCY },
    ],
    columns: [
      { key: "title", labelKey: "finance.reports.resource.activityBudget.line", source: "budget", valueType: ReportValueType.TEXT },
      { key: "type", labelKey: "finance.reports.resource.activityBudget.type", source: "budget", valueType: ReportValueType.TEXT },
      { key: "planned", labelKey: "finance.reports.resource.activityBudget.planned", source: "budget", valueType: ReportValueType.CURRENCY },
      { key: "actual", labelKey: "finance.reports.resource.activityBudget.actual", source: "budget", valueType: ReportValueType.CURRENCY },
      { key: "variance", labelKey: "finance.reports.resource.activityBudget.variance", source: "budget", valueType: ReportValueType.CURRENCY },
    ],
    rows: data.lines.map((line) => ({
      title: line.title,
      type: line.type,
      planned: line.planned,
      actual: line.actual,
      variance: line.variance,
    })),
  }
}

/** Per-department income/expense/net → a table keyed by department. */
export function adaptDepartmentSummary(data: DepartmentSummaryResponse): NormalizedReport {
  return {
    meta: { ...meta("department"), datasetKind: ReportDatasetKind.GROUPED },
    columns: [
      { key: "departmentName", labelKey: "finance.reports.resource.departmentSummary.department", source: "department", valueType: ReportValueType.TEXT },
      { key: "income", labelKey: "finance.reports.resource.departmentSummary.income", source: "department", valueType: ReportValueType.CURRENCY },
      { key: "expense", labelKey: "finance.reports.resource.departmentSummary.expense", source: "department", valueType: ReportValueType.CURRENCY },
      { key: "net", labelKey: "finance.reports.resource.departmentSummary.net", source: "department", valueType: ReportValueType.CURRENCY },
    ],
    rows: data.items.map((item) => ({
      departmentName: item.departmentName,
      income: item.income,
      expense: item.expense,
      net: item.net,
    })),
  }
}
